/**
 * Движение фасадов мебели из конструктора: дверца поворачивается на петлях, подъёмник
 * и дверца посудомойки — вокруг горизонтальной оси, ящик выезжает вперёд. Деталь хранит,
 * как она движется (`PartMotion`), а положение при открытии считает `movedPart` — без
 * three.js, обычной математикой поворота вокруг оси.
 */

export type MotionVec = [number, number, number];
export type MotionAxis = "x" | "y" | "z";

export type PartMotion =
	/** Поворот вокруг оси через точку `pivot` (м) на угол `angle` (рад) при полном открытии */
	| { kind: "hinge"; axis: MotionAxis; pivot: MotionVec; angle: number }
	/** Сдвиг на `offset` (м) при полном открытии */
	| { kind: "slide"; offset: MotionVec };

/** Поворот вектора вокруг оси по правилу правой руки — так же, как поворачивает three.js */
export const rotateAbout = ([x, y, z]: MotionVec, axis: MotionAxis, angle: number): MotionVec => {
	const c = Math.cos(angle);
	const s = Math.sin(angle);
	if (axis === "x") return [x, y * c - z * s, y * s + z * c];
	if (axis === "y") return [x * c + z * s, y, -x * s + z * c];
	return [x * c - y * s, x * s + y * c, z];
};

/**
 * Угол открывания со знаком: створка, что от петли смотрит в `from`, поворачивается
 * к `to` (наружу, в комнату), а не внутрь корпуса.
 */
export const swingAngle = (from: MotionVec, to: MotionVec, axis: MotionAxis, angle: number) => {
	const [x, y, z] = rotateAbout(from, axis, Math.PI / 2);
	return x * to[0] + y * to[1] + z * to[2] >= 0 ? angle : -angle;
};

/** Положение и поворот детали, открытой на долю `open` (0 — закрыто, 1 — открыто) */
export const movedPart = (
	position: MotionVec,
	motion: PartMotion | undefined,
	open: number,
): { position: MotionVec; rotation: MotionVec } => {
	if (!motion || open <= 0) return { position, rotation: [0, 0, 0] };
	if (motion.kind === "slide") {
		return {
			position: [
				position[0] + motion.offset[0] * open,
				position[1] + motion.offset[1] * open,
				position[2] + motion.offset[2] * open,
			],
			rotation: [0, 0, 0],
		};
	}
	const angle = motion.angle * open;
	const { pivot } = motion;
	const [x, y, z] = rotateAbout(
		[position[0] - pivot[0], position[1] - pivot[1], position[2] - pivot[2]],
		motion.axis,
		angle,
	);
	return {
		position: [pivot[0] + x, pivot[1] + y, pivot[2] + z],
		rotation:
			motion.axis === "x" ? [angle, 0, 0] : motion.axis === "y" ? [0, angle, 0] : [0, 0, angle],
	};
};

/** Сдвиг движения вместе с деталью — когда часть кухни перецентрируется */
export const shiftMotion = (
	motion: PartMotion | undefined,
	dx: number,
	dz: number,
): PartMotion | undefined =>
	motion?.kind === "hinge"
		? { ...motion, pivot: [motion.pivot[0] + dx, motion.pivot[1], motion.pivot[2] + dz] }
		: motion;
